/**
 * The questions anybody asks of the payment region, asked once.
 *
 * The region is four words again (register item 51, 2026-09-22): the money
 * going in, and nothing about it coming back. The five refund states it grew
 * for the review and the claim (PAY-013, PAY-030) went with the machine's
 * review events - a refund is the thin refund case's (`OrderRefunds`), so the
 * two projections that asked "did money go back" (`paymentRefunded`,
 * `paymentRefundedInFull`) and the one that asked "is an answer still owed"
 * (`paymentOwesAnAnswer`, now the same sentence as `paymentHolding`) went too.
 *
 * Both that remain are switches over the union rather than lists of interesting
 * names, so a new `PaymentPhase` is a typecheck failure here instead of a
 * silent no.
 */

import type { PaymentPhase } from '../enums';

/**
 * The platform is HOLDING the guest's money right now (PAY-002).
 *
 * The wire twin of `paymentHolding` in `packages/domain/src/order-axes.ts`,
 * which is what the machine's `mayOffer` gate asks before it will let any
 * counter be handed the order. Contracts cannot import domain - this package is
 * what the frontends share - so the two are pinned against each other by the
 * reachability walk in `reallocation.test.ts` (`packages/services`), over every
 * state the machine can actually reach rather than per name.
 */
export function paymentHolding(payment: PaymentPhase): boolean {
  switch (payment) {
    case 'PAID':
      return true;
    case 'PENDING':
    case 'FAILED':
      return false;
  }
}

/**
 * This order cannot move on the money side without a human: a card refused
 * while the bag is still promised (PAY-013). A refund waiting on a person is
 * the refund case's queue, not this region's.
 */
export function paymentNeedsOperator(payment: PaymentPhase): boolean {
  switch (payment) {
    case 'FAILED':
      return true;
    case 'PENDING':
    case 'PAID':
      return false;
  }
}
